// ===============================
// useVerticalPricing Hook
// Vertical prices and per-tier vertical limits from the Mothership API.
// Single source of truth: https://api.axoix.in
// ===============================

import { useEffect, useMemo, useState } from 'react';
import {
  fetchVerticals,
  fetchPricingTiers,
  VerticalPricingAPI,
} from '../services/api/pricingService';

/**
 * What signup and checkout need to price an order.
 *
 * Both used to import `getVerticalPrice()` and `TIER_VERTICAL_LIMITS` from
 * `api/pricing.tsx`, where the price was derived as Rs 10,000 x (1 - 60%).
 * That produced Rs 4,000, which matched the API only by arithmetic
 * coincidence — nothing kept the two in step. The cart total was computed
 * from the constant while the Mothership prices the real order from
 * `pricing_tiers`, so a price change on one side would have shown the
 * customer one number and charged another.
 */
export interface VerticalPricingData {
  /** Rs/month for one additional vertical. Null until loaded. */
  verticalMonthlyPrice: number | null;
  /** Rs/year for one additional vertical. Null until loaded. */
  verticalYearlyPrice: number | null;
  /** How many verticals each tier may hold, keyed by tier id. */
  tierVerticalLimits: Record<string, number>;
  /** Priced verticals, in the API's display order. */
  verticals: VerticalPricingAPI[];
  loading: boolean;
  error: Error | null;
  /** False when serving the built-in fallbacks rather than API data. */
  isFromAPI: boolean;
}

/**
 * Used only when the API cannot be reached. These are the values that were
 * live on 2026-09-08 — deliberately the CURRENT prices and not the planned
 * ones, so an outage during a price change degrades to "slightly stale"
 * rather than "quotes a price we do not honour".
 *
 * A quoted total is a promise, so when these are in use the caller should
 * say the figure is indicative — see `isFromAPI`.
 */
const FALLBACK_VERTICAL_MONTHLY = 500;
const FALLBACK_VERTICAL_YEARLY = 5000;
const FALLBACK_TIER_LIMITS: Record<string, number> = {
  explorer: 1,
  visitor: 1, // legacy alias for explorer
  officer: 2,
  commander: 5,
  titan: 10,
};

export const useVerticalPricing = (): VerticalPricingData => {
  const [verticals, setVerticals] = useState<VerticalPricingAPI[] | null>(null);
  const [tierLimits, setTierLimits] = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        // Both or neither: a cart that knows the price but not the limit can
        // still offer a vertical the tier may not hold.
        const [vs, tiers] = await Promise.all([
          fetchVerticals(),
          fetchPricingTiers(),
        ]);
        if (!mounted) return;

        setVerticals(vs);

        // `featureLimits.verticals` is the same field tier_limits.py enforces
        // on the server, so the picker cannot offer what the API would refuse.
        const limits: Record<string, number> = {};
        for (const t of tiers) {
          const raw = (t.featureLimits || {})['verticals'];
          const n = typeof raw === 'number' ? raw : Number(raw);
          if (Number.isFinite(n)) limits[t.id] = n;
        }
        if (limits.explorer !== undefined && limits.visitor === undefined) {
          limits.visitor = limits.explorer;
        }
        setTierLimits(Object.keys(limits).length > 0 ? limits : null);
        setError(null);
      } catch (err) {
        console.error('[useVerticalPricing] Failed to fetch from API:', err);
        if (mounted) setError(err as Error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(() => {
    // Every vertical is priced the same today (VERTICAL_PRICES in shop.py is
    // a flat rate), but the API returns a price per vertical because that is
    // expected to change. Read the first rather than assuming a constant.
    const first = verticals && verticals.length > 0 ? verticals[0] : null;

    return {
      verticalMonthlyPrice: first ? first.monthly_price : FALLBACK_VERTICAL_MONTHLY,
      verticalYearlyPrice: first ? first.yearly_price : FALLBACK_VERTICAL_YEARLY,
      tierVerticalLimits: tierLimits ?? FALLBACK_TIER_LIMITS,
      verticals: verticals ?? [],
      loading,
      error,
      isFromAPI: verticals !== null && tierLimits !== null,
    };
  }, [verticals, tierLimits, loading, error]);
};

export default useVerticalPricing;
